/**
 * Area (a) · Création back-office (BO-*, Flows-Etats, Flows-Invalidations, BO-Mix-Client).
 * OWNED BY the area-creation agent (see admin/README.md « Areas & ownership »).
 *
 * The dossier the back-office drives is the client's tracking record (`Tracking`, shared/creation-tracking.ts): the
 * fiche shows `tracking` as is, so what the agent does is exactly what the client app shows next. Every action maps
 * 1:1 onto a service function of api/src/domains/creation-tracking/backoffice.ts (api/docs/backoffice.md) when one
 * exists — same name, same semantics, same e-mails. Actions marked « API gap » have no service function yet: the
 * admin pass must write them (listed in the comment of each endpoint).
 *
 * Rights: reads need `creation:read` (COMPTABLE: attached companies only); actions need `creation:write` AND the
 * dossier assigned to the signed-in member (ADMIN: any assigned dossier) — an unassigned dossier is read-only for
 * everyone until « Prendre en charge ». Admin-only actions (reject by Melrys, forced transition, manual payment)
 * need `creation.adminActions`.
 */
import { z } from "zod";
import { adminEndpoint } from "../endpoint.ts";
import { Cents, Email, Id, IsoDate, IsoDateTime, Ok } from "../common.ts";
import type { EndpointGroup } from "../shell.ts";
import { LegalForm } from "../shared/company.ts";
import {
  AnswerChange,
  CancellationQuote,
  CorrectionFieldType,
  CorrectionReason,
  FileRef,
  GeneratedDocumentFile,
  InpiPointKind,
  PaymentKind,
  Tracking,
  TrackingState,
  UploadInput,
} from "../shared/creation-tracking.ts";

// ───────────────────────── Lists (BO-Creation-*) ─────────────────────────

export const BoTab = z.enum(["nouvelles", "en-attente", "attente-client", "terminees"]);
export type BoTab = z.infer<typeof BoTab>;

/** « En attente de » chip: Melrys (amber), Client / INPI (grey), Clos (ended). */
export const WaitingOn = z.enum(["melrys", "client", "inpi", "done"]);
export type WaitingOn = z.infer<typeof WaitingOn>;

export const BoAgent = z.object({ id: Id, name: z.string(), roleLabel: z.string() });
export type BoAgent = z.infer<typeof BoAgent>;

export const BoEndingSummary = z.object({
  kind: z.enum(["cancelled", "rejected_by_melrys", "rejected_by_inpi", "closed_inactive"]),
  at: IsoDate,
  reason: z.string().nullable(),
  refundPercent: z.number().int().nullable(),
  refundCents: Cents.nullable(),
  refundStatus: z.enum(["none", "pending", "issued"]).nullable(),
});

export const BoDossierRow = z.object({
  id: Id,
  reference: z.string(),
  companyId: Id,
  /** « GFA Conseil » for a company, the entrepreneur's name for an AE. */
  displayName: z.string(),
  /** Under the name: activity for an AE (« Graphisme freelance »), legal form for a company (« SASU »). */
  subtitle: z.string(),
  legalForm: LegalForm,
  ownerName: z.string(),
  submittedAt: IsoDateTime,
  state: TrackingState,
  phase: z.number().int(),
  phaseCount: z.number().int(),
  phaseLabel: z.string(),
  /** Sub-state in the « Étape » column (« À prévalider », « Prêt à déposer »…). */
  stateLabel: z.string(),
  waitingOn: WaitingOn,
  withAccounting: z.boolean(),
  /** Accounting chip on decided dossiers: « Compta ouverte » / « Sans compta » (null before the decision). */
  accountingLabel: z.string().nullable(),
  agent: BoAgent.nullable(),
  /** Number of services of the client account (≥ 2: « Compte · n services » link to BO-Mix-Client). */
  accountServices: z.number().int(),
  ending: BoEndingSummary.nullable(),
});
export type BoDossierRow = z.infer<typeof BoDossierRow>;

export const BoListQuery = z.object({
  tab: BoTab,
  type: z.enum(["all", "ae", "company"]).default("all"),
});

export const BoListResponse = z.object({
  items: z.array(BoDossierRow),
  /** Tab counters (« Nouvelles demandes 3 · Demandes en attente 11 · … »), per type filter. */
  tabs: z.record(BoTab, z.number().int()),
});
export type BoListResponse = z.infer<typeof BoListResponse>;

// ───────────────────────── Fiche (BO-Tiroir-*) ─────────────────────────

/** One answer of the client's questionnaire as shown in « Formulaire du demandeur ». */
export const BoFormRow = z.object({
  id: Id,
  label: z.string(),
  value: z.string(),
  /** Eligibility answers are checked by hand (chip « Revue manuelle »), never invalidated. */
  manualReview: z.boolean(),
  /** How the client answers if invalidated (correction window field). */
  fieldType: CorrectionFieldType,
  options: z.array(z.string()).optional(),
  /** APE code of an activity line. */
  ape: z.string().nullable(),
  /**
   * The row is a questionnaire question (shared/creation-questions.ts, key = `id`): invalidated, the client answers
   * with the questionnaire's own input and rules, and the corrected value is written back into the answers.
   */
  question: z.boolean().optional(),
  /**
   * Not correctable through a questionnaire correction (shared/creation-questions.ts `lockOf` / `KYC_LOCK`): why, and
   * where it is fixed instead (`source`: the question it derives from, `piece`: piece invalidation, `account`: the
   * client's account / support). No « Invalider » on such a row.
   */
  locked: z.object({ via: z.enum(["source", "piece", "account"]), reason: z.string(), sourceKey: z.string().optional() }).nullable().optional(),
});
export type BoFormRow = z.infer<typeof BoFormRow>;

export const BoFormSection = z.object({
  id: Id,
  /** « Étape 3 · Associé et président ». */
  title: z.string(),
  /** Short section name used in the client's correction window (« Associé et président »). */
  section: z.string(),
  rows: z.array(BoFormRow),
  /** Activités: secondary activities (numbered, collapsible after 4). */
  secondary: z.array(BoFormRow),
});
export type BoFormSection = z.infer<typeof BoFormSection>;

/** A row / piece marked « Invalider » but not sent yet (« À envoyer »): sent together by « Envoyer les corrections ». */
export const BoDraftInvalidation = z.object({
  /** Form row id or piece id. */
  targetId: Id,
  target: z.enum(["field", "piece"]),
  section: z.string(),
  label: z.string(),
  currentAnswer: z.string(),
  reason: CorrectionReason,
  comment: z.string(),
  fieldType: CorrectionFieldType,
  options: z.array(z.string()).optional(),
  /** Questionnaire question the correction item answers (creation-questions.ts key), null for a piece / free row. */
  key: z.string().nullable().optional(),
  createdAt: IsoDateTime,
});
export type BoDraftInvalidation = z.infer<typeof BoDraftInvalidation>;

export const BoPieceStatus = z.enum(["to_check", "validated", "to_send", "to_replace", "missing"]);
export const BoPiece = z.object({
  id: Id,
  title: z.string(),
  /** « Élise Desgranges » after the title of an identity piece. */
  person: z.string().nullable(),
  kind: z.enum(["id_card", "address_proof", "domiciliation", "capital_certificate", "residence_permit", "tax_residence", "other"]),
  status: BoPieceStatus,
  file: FileRef.nullable(),
  refusal: z.string().nullable(),
  /** Pieces of the finalisation (tracking.finalization.pieces) are reviewed with reviewPiece; the others go through a correction request. */
  source: z.enum(["form", "capital", "finalization"]),
  versions: z.number().int(),
});
export type BoPiece = z.infer<typeof BoPiece>;

export const BoGeneratedDoc = z.object({
  id: Id,
  title: z.string(),
  kind: z.enum(["statuts_draft", "statuts", "subscribers_list", "power_of_attorney", "non_conviction", "domiciliation_attestation", "kbis", "inpi_receipt", "sirene"]),
  status: z.enum(["to_generate", "to_sign", "partially_signed", "signed", "to_upload", "received"]),
  date: IsoDate.nullable(),
});
export type BoGeneratedDoc = z.infer<typeof BoGeneratedDoc>;

export const BoReviewDecision = z.object({
  itemId: Id,
  decision: z.enum(["validated", "reinvalidated"]),
  reason: CorrectionReason.nullable(),
  comment: z.string().nullable(),
  /** Annotated proof joined to a re-invalidation (« Joindre une pièce annotée », BO-Revue-Reinvalider). */
  attachment: FileRef.nullable().optional(),
  at: IsoDateTime,
});
export type BoReviewDecision = z.infer<typeof BoReviewDecision>;

export const BoChecklistItem = z.object({
  id: Id,
  group: z.enum(["Identité", "Capital", "Siège", "INPI", "Documents"]),
  label: z.string(),
  checkedBy: z.string().nullable(),
  checkedAt: IsoDateTime.nullable(),
});
export type BoChecklistItem = z.infer<typeof BoChecklistItem>;

/**
 * One point of an INPI request (C15a, retour 3: one correction carries several points). `id` is the point's
 * correction item id.
 */
export const BoInpiRequest = z.object({
  id: Id,
  message: z.string(),
  /** C15a (retour 3): « Pièce jointe » or « Précision textuelle » (« Autre » removed, 3e réponses 04/11). Absent: recorded before C15a (a document). */
  kind: InpiPointKind.optional(),
  dueDate: IsoDate,
  createdAt: IsoDateTime,
  status: z.enum(["sent", "answered", "forwarded", "expired", "closed"]),
  answeredAt: IsoDateTime.nullable(),
  forwardedAt: IsoDateTime.nullable(),
});
export type BoInpiRequest = z.infer<typeof BoInpiRequest>;

export const BoPaymentLink = z.object({
  /** When the e-mail with the link went out, and to whom. */
  sentAt: IsoDateTime.nullable(),
  email: z.string().nullable(),
  description: z.string().nullable(),
  /** Paid outside Stancer, confirmed by hand (« paiement reçu hors Stancer ? »). */
  manual: z.boolean(),
});

export const BoDossier = z.object({
  id: Id,
  reference: z.string(),
  displayName: z.string(),
  legalForm: LegalForm,
  activity: z.string(),
  applicant: z.object({ firstName: z.string(), lastName: z.string(), email: Email, phone: z.string() }),
  submittedAt: IsoDateTime,
  /** What the client app shows: the shared tracking aggregate. */
  tracking: Tracking,
  agent: BoAgent.nullable(),
  /** The signed-in member may act on this dossier (assigned, write rights). */
  canAct: z.boolean(),
  /** Why not, in product words (« Dossier non attribué… », « Lecture seule… »). */
  readOnlyReason: z.string().nullable(),
  waitingOn: WaitingOn,
  stateLabel: z.string(),
  form: z.array(BoFormSection),
  drafts: z.array(BoDraftInvalidation),
  /** Decisions of the « Revue des réponses du client » on the open correction (auto-saved). */
  review: z.array(BoReviewDecision),
  /** Questionnaire answers rewritten by the client's corrections (previous → corrected, who / when, review), oldest first. */
  answerChanges: z.array(AnswerChange),
  pieces: z.array(BoPiece),
  generatedDocs: z.array(BoGeneratedDoc),
  legalNoticeLink: BoPaymentLink,
  legalNoticeAttestation: z.object({ file: FileRef, journal: z.string(), reference: z.string(), publishedAt: IsoDate, status: z.enum(["to_validate", "validated"]) }).nullable(),
  registry: z.object({
    /** « Greffe du tribunal de commerce de Bordeaux », resolved from the registered office. */
    court: z.string(),
    link: BoPaymentLink,
    invoiceNumber: z.string().nullable(),
    receipt: FileRef.nullable(),
    /** « Déposer à l'INPI · Valider » done: the drawer moves on to « Marquer déposé ». */
    depositOpened: z.boolean(),
  }),
  checklist: z.array(BoChecklistItem),
  inpiRequests: z.array(BoInpiRequest),
  /**
   * Filing made through the Guichet unique API (null: manual filing / not filed): its formality and the INPI fees.
   * `fees.status` « pending »: the formality waits in PAYMENT_PENDING until « Frais INPI réglés » (pre-funded INPI account).
   */
  inpiFiling: z
    .object({
      formalityId: z.string(),
      fees: z.object({ status: z.enum(["pending", "paid"]), since: IsoDateTime, paidAt: IsoDateTime.nullable(), paidBy: z.string().nullable() }).nullable(),
    })
    .nullable(),
  /** The INPI accepted (« L'INPI a validé ») and the registration (SIREN) is still to record. */
  inpiAccepted: z.boolean(),
  officialDocs: z.object({ kbis: FileRef.nullable(), inpiReceipt: FileRef.nullable(), sirene: FileRef.nullable() }),
  /** Data shown in the document previews (capital certificate, statuts, invoices). */
  capitalInfo: z.object({ subscribedCents: Cents, paidUpCents: Cents, paidUpPercent: z.number().int(), shares: z.number().int(), bank: z.string().nullable() }).nullable(),
  creationInvoiceNumber: z.string().nullable(),
  accountServices: z.number().int(),
  /**
   * The identity the dossier declares (questionnaire + back-office APE): prefills « Faire manuellement » and is what
   * the registry fiche is compared with. Optional (additive).
   */
  declaredIdentity: z
    .object({ name: z.string(), legalForm: z.string(), address: z.string(), capitalCents: Cents.nullable(), ape: z.string(), apeLabel: z.string() })
    .optional(),
});
export type BoDossier = z.infer<typeof BoDossier>;

const D = z.object({ dossierId: Id });
const B = "/admin/creation/dossiers/:dossierId";
const W = ["creation", "write"] as const;
const R = ["creation", "read"] as const;
const ADMIN_ONLY = ["creation.adminActions", "write"] as const;

/** One item of a correction request as the agent writes it (requestInformation's `items`). */
export const BoCorrectionInput = z.object({
  targetId: Id,
  reason: CorrectionReason,
  comment: z.string().trim().min(1, { message: "Écrivez un message pour le client." }).max(1000),
});

/** C15a (retour 3): one point of an INPI request as the agent writes it (recordInpiRequest's `points`). */
export const BoInpiPointInput = z.object({
  kind: InpiPointKind,
  message: z.string().trim().min(1, { message: "Recopiez la demande de l'INPI." }).max(1000),
});
export type BoInpiPointInput = z.infer<typeof BoInpiPointInput>;

/** Identity fields compared between the registry and the dossier (« Écart avec le dossier »). */
export const RegistryField = z.enum(["name", "legalForm", "address", "capital", "ape"]);
export type RegistryField = z.infer<typeof RegistryField>;

/**
 * Registry fiche of a SIREN. Every value comes from the registry, never from the dossier: `source` says which one —
 * « insee » (API Sirene, live), « datakey » (Data&Key, live) or « simulation » (dev / tests only, never in production).
 * Values the registry does not publish are empty (`capitalCents` null: the INSEE has no capital; `withheld`: fields
 * the INSEE does not disclose, « diffusion partielle »). Additive fields are optional (older mocks).
 */
export const BoRegistryLookup = z.object({
  siren: z.string(),
  name: z.string(),
  legalForm: z.string(),
  address: z.string(),
  capitalCents: Cents.nullable(),
  registeredAt: IsoDate,
  ape: z.string(),
  apeLabel: z.string(),
  siret: z.string(),
  documents: z.array(
    z.object({
      kind: z.enum(["kbis", "inpi_receipt", "sirene"]),
      title: z.string(),
      /**
       * « fetched »: retrieved by the provider (deposited on « Enregistrer »); « available »: the issuer publishes it,
       * Melrys downloads it on « Enregistrer » (`url` = the public document); « to_upload »: no provider delivers it —
       * Melrys deposits it by hand. Absent = « fetched » (older mocks).
       */
      status: z.enum(["fetched", "available", "to_upload"]).optional(),
      url: z.string().url().nullable().optional(),
    }),
  ),
  source: z.enum(["insee", "datakey", "simulation"]).optional(),
  /** INSEE « catégorie juridique » code (« 5710 »), null when unknown. */
  legalFormCode: z.string().nullable().optional(),
  /** Administrative state of the legal unit (false: ceased / struck off). */
  active: z.boolean().optional(),
  /** Fields the registry does not disclose (INSEE « diffusion partielle »): the agent completes them from the dossier. */
  withheld: z.array(RegistryField).optional(),
  /** Registry ≠ dossier: shown as warnings, the agent decides what is recorded. */
  differences: z.array(z.object({ field: RegistryField, label: z.string(), registry: z.string(), dossier: z.string() })).optional(),
});
export type BoRegistryLookup = z.infer<typeof BoRegistryLookup>;

// ───────────────────────── Compte client (BO-Mix-Client) ─────────────────────────

export const ClientService = z.object({
  kind: z.enum(["creation", "accounting", "invoicing"]),
  /** Chip on the service button: « Terminée », « En cours », « Active », « PRO · annuel », « Suspendue »… */
  status: z.string(),
  tone: z.enum(["success", "info", "warning", "danger", "neutral"]),
  /** Optional banner explaining a service in trouble. */
  banner: z.string().nullable(),
  note: z.string(),
  rows: z.array(z.object({ label: z.string(), value: z.string() })),
  action: z.object({ label: z.string(), href: z.string() }).nullable(),
  timeline: z.array(z.object({ title: z.string(), detail: z.string(), date: IsoDate, done: z.boolean() })),
});
export type ClientService = z.infer<typeof ClientService>;

export const ClientAccount = z.object({
  companyId: Id,
  name: z.string(),
  legalForm: z.string(),
  siret: z.string().nullable(),
  owner: z.object({ firstName: z.string(), lastName: z.string(), email: Email }),
  clientSince: IsoDate,
  services: z.array(ClientService),
  company: z.array(z.object({ label: z.string(), value: z.string() })),
  /** Link to the Compta client file (Admin-Client-Fiche) when the company is an accounting client. */
  clientFileHref: z.string().nullable(),
});
export type ClientAccount = z.infer<typeof ClientAccount>;

export const creationAdminApi = {
  // ── Reads ──
  list: adminEndpoint({ id: "admin.creation.list", method: "GET", path: "/admin/creation/dossiers", query: BoListQuery, response: BoListResponse, permission: R }),
  get: adminEndpoint({ id: "admin.creation.get", method: "GET", path: B, params: D, response: BoDossier, permission: R }),
  /** Active ADMIN / AGENT members (« En charge » filter, « Réattribuer à un agent »). */
  agents: adminEndpoint({ id: "admin.creation.agents", method: "GET", path: "/admin/creation/agents", response: z.object({ items: z.array(BoAgent) }), permission: R }),
  /** Board 96 grid applied to the dossier (BO-Annuler-Demande). = the client's `tracking.cancellation.quote`. */
  cancellationQuote: adminEndpoint({ id: "admin.creation.cancellationQuote", method: "GET", path: `${B}/cancellation`, params: D, response: CancellationQuote, permission: R }),
  /**
   * PDF of a generated document (`generatedDocs[].id`: doc_statuts_draft, doc_sig_*, doc_sig_host): the signed version
   * (signatures + « Attestation de signature électronique ») once every signer signed. = the client's `tracking.documents.file`.
   */
  documentFile: adminEndpoint({
    id: "admin.creation.documentFile", method: "GET", path: `${B}/documents/:documentId/file`, params: D.extend({ documentId: Id }), response: GeneratedDocumentFile, permission: R,
  }),
  /** The file the client uploaded for a piece (`pieces[].id`: pc_capital, pc_id_*, pc_siege…, latest version), to review the real document. */
  pieceFile: adminEndpoint({
    id: "admin.creation.pieceFile", method: "GET", path: `${B}/pieces/:pieceId/file`, params: D.extend({ pieceId: Id }),
    response: z.object({ url: z.string(), fileName: z.string(), mimeType: z.string() }), permission: R,
  }),
  /** The file the client sent to answer a correction item (INPI request, document item): latest version. */
  correctionFile: adminEndpoint({
    id: "admin.creation.correctionFile", method: "GET", path: `${B}/corrections/:itemId/file`, params: D.extend({ itemId: Id }),
    response: z.object({ url: z.string(), fileName: z.string(), mimeType: z.string() }), permission: R,
  }),

  // ── Assignment ── API gap: no creation-side assignment table (backoffice.md « Gaps » 5).
  /** « Prendre en charge » (self) and « Réattribuer à un agent » (admin). Last assignment wins. */
  assign: adminEndpoint({ id: "admin.creation.assign", method: "POST", path: `${B}/assign`, params: D, body: z.object({ agentId: Id }), response: BoDossier, permission: W }),

  // ── Phase 2 — Melrys review ──
  /** = approvePrecheck(ctx, id, { cosign }). PRECHECK_SUBMITTED / MELRYS_INFO_RECEIVED → PRECHECK_APPROVED (AE → FINALIZATION_IN_PROGRESS). */
  approvePrecheck: adminEndpoint({ id: "admin.creation.approvePrecheck", method: "POST", path: `${B}/precheck/approve`, params: D, body: z.object({ cosign: z.boolean().optional() }), response: BoDossier, permission: W }),
  /** API gap: drafts of invalidations kept server-side (« À envoyer », survive a reload). */
  saveDraft: adminEndpoint({
    id: "admin.creation.saveDraft", method: "PUT", path: `${B}/correction-drafts/:targetId`, params: D.extend({ targetId: Id }),
    body: z.object({ target: z.enum(["field", "piece"]), reason: CorrectionReason, comment: BoCorrectionInput.shape.comment }), response: BoDossier, permission: W,
  }),
  deleteDraft: adminEndpoint({ id: "admin.creation.deleteDraft", method: "DELETE", path: `${B}/correction-drafts/:targetId`, params: D.extend({ targetId: Id }), response: BoDossier, permission: W }),
  /**
   * « Envoyer les corrections » = requestInformation(ctx, id, { source: "melrys", items, dueDate }) for the form rows and
   * form pieces; finalisation pieces go through reviewPiece(…, { validated: false, refusal }). Sends every draft at once.
   */
  requestInformation: adminEndpoint({
    id: "admin.creation.requestInformation", method: "POST", path: `${B}/corrections`, params: D,
    body: z.object({ dueDate: IsoDate }), response: z.object({ sent: z.number().int(), dossier: BoDossier }), permission: W,
  }),
  /** API gap: item-level decision of « Revue des réponses du client » (auto-saved, nothing reaches the client before completeReview). */
  reviewCorrectionItem: adminEndpoint({
    id: "admin.creation.reviewCorrectionItem", method: "PUT", path: `${B}/corrections/:requestId/items/:itemId/review`, params: D.extend({ requestId: Id, itemId: Id }),
    body: z.object({
      decision: z.enum(["validated", "reinvalidated", "pending"]),
      reason: CorrectionReason.nullable(),
      comment: z.string().max(1000).nullable(),
      /** Optional annotated proof of a re-invalidation (PDF / JPEG / PNG, 10 Mo; multipart in live mode). */
      attachment: UploadInput.nullable().optional(),
    }),
    response: BoDossier, permission: W,
  }),
  /**
   * API gap: « Terminer la revue ». All validated → the correction closes and the dossier returns to its origin state
   * (PRECHECK_SUBMITTED, « À prévalider »; finalisation states are kept). Any re-invalidated → requestInformation with those items.
   */
  completeReview: adminEndpoint({ id: "admin.creation.completeReview", method: "POST", path: `${B}/corrections/:requestId/complete`, params: D.extend({ requestId: Id }), response: BoDossier, permission: W }),

  // ── Phase 3 / 4 ──
  /** = reviewCapital(ctx, id, { accepted } | { accepted: false, reason }). */
  reviewCapital: adminEndpoint({
    id: "admin.creation.reviewCapital", method: "POST", path: `${B}/capital/review`, params: D,
    body: z.object({ accepted: z.boolean(), reason: z.string().trim().max(1000).nullable() }), response: BoDossier, permission: W,
  }),
  /** = reviewPiece(ctx, id, pieceId, { validated } | { validated: false, refusal }). Form pieces: API gap (validated flag only). */
  reviewPiece: adminEndpoint({
    id: "admin.creation.reviewPiece", method: "POST", path: `${B}/pieces/:pieceId/review`, params: D.extend({ pieceId: Id }),
    body: z.object({ validated: z.boolean(), refusal: z.string().nullable() }), response: BoDossier, permission: W,
  }),
  /** = openLegalNotice(ctx, id): « Contrôler le dossier final » done. FINAL_SUBMITTED → LEGAL_NOTICE_PREPARATION (AE → READY_TO_FILE). */
  openLegalNotice: adminEndpoint({ id: "admin.creation.openLegalNotice", method: "POST", path: `${B}/legal-notice/open`, params: D, response: BoDossier, permission: W }),

  // ── Phase 5 — legal notice ──
  /**
   * = quoteLegalNotice(ctx, id, { amountHtCents, journal }) — « Créer un lien de paiement JAL » (FOR-06).
   * API gap: the function only accepts a notice in `quote`; the cabinet creates the link from `documents_review` / `ready` too.
   * 2c (réponses 04/11): `amountHtCents` is the journal's price HT; the client pays HT + TVA 20 % (rounded to the cent).
   */
  quoteLegalNotice: adminEndpoint({
    id: "admin.creation.quoteLegalNotice", method: "POST", path: `${B}/legal-notice/quote`, params: D,
    body: z.object({ amountHtCents: Cents.positive(), journal: z.string().trim().min(2), description: z.string().max(200).nullable() }), response: BoDossier, permission: W,
  }),
  /** = renewPaymentLink(ctx, id, kind) — « Renvoyer le lien » (FOR-12). */
  renewPaymentLink: adminEndpoint({ id: "admin.creation.renewPaymentLink", method: "POST", path: `${B}/payments/:kind/renew`, params: D.extend({ kind: PaymentKind }), response: BoDossier, permission: W }),
  /** API gap: « paiement reçu hors Stancer ? » / « Confirmer le paiement (ops manuel) » — replays the webhook's effect. Admin only. */
  confirmPayment: adminEndpoint({ id: "admin.creation.confirmPayment", method: "POST", path: `${B}/payments/:kind/confirm`, params: D.extend({ kind: PaymentKind }), response: BoDossier, permission: ADMIN_ONLY }),
  /** API gap: « + Ajouter l'attestation » (the parution certificate file, before its validation). */
  uploadLegalNoticeAttestation: adminEndpoint({
    id: "admin.creation.uploadLegalNoticeAttestation", method: "POST", path: `${B}/legal-notice/attestation`, params: D,
    body: z.object({
      name: z.string().min(1), sizeBytes: z.number().int().nonnegative(), mimeType: z.string(),
      journal: z.string().trim().min(2), reference: z.string().trim().min(2, { message: "Référence de parution requise." }), publishedAt: IsoDate,
    }),
    response: BoDossier, permission: W,
  }),
  /** = publishLegalNotice(ctx, id, { journal, reference, publishedAt }) — « Valider l'attestation ». */
  publishLegalNotice: adminEndpoint({
    id: "admin.creation.publishLegalNotice", method: "POST", path: `${B}/legal-notice/publish`, params: D,
    body: z.object({ journal: z.string().min(2), reference: z.string().min(2), publishedAt: IsoDate }), response: BoDossier, permission: W,
  }),
  /** API gap 1 (backoffice.md): published notice → READY_TO_FILE + notifyReadyToFile (FOR-07 + NOT-F05). « Passer à : Dépôt INPI ». */
  completeLegalNotice: adminEndpoint({ id: "admin.creation.completeLegalNotice", method: "POST", path: `${B}/legal-notice/complete`, params: D, response: BoDossier, permission: W }),

  // ── Phase 6 — INPI filing ──
  /**
   * = openRegistryFees(ctx, id) (FOR-12). API gap: the amount is typed by the agent (the function uses the 2026 tariff).
   * 2c (réponses 04/11): `amountCents` is TTC (paid by the client); HT = TTC ÷ 1,2 rounded, TVA = TTC − HT.
   */
  openRegistryFees: adminEndpoint({
    id: "admin.creation.openRegistryFees", method: "POST", path: `${B}/registry-fees/open`, params: D,
    body: z.object({ amountCents: Cents.positive(), description: z.string().max(200).nullable() }), response: BoDossier, permission: W,
  }),
  /** API gap: « Déposer à l'INPI · Valider » once the registry fees are paid (opens the filing checklist). */
  openFiling: adminEndpoint({ id: "admin.creation.openFiling", method: "POST", path: `${B}/inpi/open`, params: D, response: BoDossier, permission: W }),
  /** API gap: « Checklist avant dépôt INPI », each point traced (who, when). */
  setChecklistItem: adminEndpoint({
    id: "admin.creation.setChecklistItem", method: "PUT", path: `${B}/inpi/checklist/:itemId`, params: D.extend({ itemId: Id }),
    body: z.object({ checked: z.boolean() }), response: BoDossier, permission: W,
  }),
  /** = fileWithInpi(ctx, id). API gap: manual filing (liasse number typed by the agent) next to the provider call. */
  fileWithInpi: adminEndpoint({
    id: "admin.creation.fileWithInpi", method: "POST", path: `${B}/inpi/file`, params: D,
    body: z.object({ liasse: z.string().trim().regex(/^[A-Z0-9]{8,20}$/, { message: "Numéro de liasse invalide (lettres majuscules et chiffres)." }), filedAt: IsoDate }),
    response: BoDossier, permission: W,
  }),

  /**
   * = fileWithInpi(ctx, id, undefined, { apeCodes }) — « Déposer via le Guichet unique »: the formality is built from the
   * questionnaire and filed through the INPI API (422 inpi_payload_incomplete when the dossier lacks a required value).
   */
  submitToInpi: adminEndpoint({ id: "admin.creation.submitToInpi", method: "POST", path: `${B}/inpi/submit`, params: D, response: BoDossier, permission: W }),
  /** = confirmInpiFeesPaid(ctx, id, staff) — « Frais INPI réglés »: the fees of an API filing were paid on the Guichet unique. */
  confirmInpiFees: adminEndpoint({ id: "admin.creation.confirmInpiFees", method: "POST", path: `${B}/inpi/fees-paid`, params: D, response: BoDossier, permission: W }),

  // ── Phase 7 / 8 — INPI verification, decision ──
  /**
   * = recordInpiRequest(ctx, id, points, dueDate) — « L'INPI demande des compléments » / « Nouvelle demande de l'INPI ».
   * C15a (retour 3): `points` — several requests of the INPI sent to the client at once, each with its type; the
   * client answers each one and sends them back together. `message` alone (older clients) = one « pièce jointe » point.
   */
  recordInpiRequest: adminEndpoint({
    id: "admin.creation.recordInpiRequest", method: "POST", path: `${B}/inpi/requests`, params: D,
    body: z.object({ message: z.string().trim().min(1).max(1000).optional(), points: z.array(BoInpiPointInput).min(1).max(10).optional(), dueDate: IsoDate }),
    response: BoDossier, permission: W,
  }),
  /** = forwardInpiResponse(ctx, id) — « Transmission d'une réponse à l'INPI · Confirmer ». */
  forwardInpiResponse: adminEndpoint({ id: "admin.creation.forwardInpiResponse", method: "POST", path: `${B}/inpi/forward`, params: D, response: BoDossier, permission: W }),
  /** API gap: « L'INPI a validé » / « Passer à : Décision » — the registration (SIREN) is recorded next. */
  recordInpiAcceptance: adminEndpoint({ id: "admin.creation.recordInpiAcceptance", method: "POST", path: `${B}/inpi/accept`, params: D, response: BoDossier, permission: W }),
  /**
   * Flags a SIREN as soon as it is typed in « Enregistrer l'immatriculation »: EXPERTISE ADMIN's own SIREN, or one
   * already held by another company on Melrys (same rule as recordCompanyCreated, which refuses them).
   */
  sirenCheck: adminEndpoint({
    id: "admin.creation.sirenCheck", method: "GET", path: "/admin/creation/siren-check", query: z.object({ siren: z.string().regex(/^\d{9}$/), dossierId: Id }),
    response: z.object({ conflict: z.object({ code: z.enum(["siren_is_melrys", "siren_taken"]), message: z.string() }).nullable() }), permission: R,
  }),
  /**
   * Registry fiche by SIREN (« Récupérer la fiche »): Data&Key when live, else the INSEE Sirene API (live), else the
   * Data&Key simulator (dev / tests, `source` « simulation »; refused in production: 502 `registry_unavailable`).
   * With `dossierId`: `differences` compares the fiche with the dossier.
   */
  registryLookup: adminEndpoint({
    id: "admin.creation.registryLookup", method: "GET", path: "/admin/creation/registry-lookup", query: z.object({ siren: z.string().regex(/^\d{9}$/), dossierId: Id.optional() }),
    response: BoRegistryLookup, permission: R,
  }),
  /**
   * = recordCompanyCreated(ctx, id, { siren, siret, ape, registeredAt, identity }) — « Enregistrer l'immatriculation ».
   * `source` given (current admin): the complete identity is required and checked like the window (SIREN / SIRET Luhn,
   * SIRET = SIREN + NIC, APE in the NAF rév. 2, date not in the future, capital for a company) and written to the
   * company, the tracking record (client « Accès rapide ») and the audit. « registry »: the INSEE « avis de situation »
   * is downloaded and deposited when the INSEE delivers it. `withDocuments` only deposits simulated files in dev / tests.
   */
  recordCompanyCreated: adminEndpoint({
    id: "admin.creation.recordCompanyCreated", method: "POST", path: `${B}/registration`, params: D,
    body: z.object({
      siren: z.string().regex(/^\d{9}$/, { message: "Le SIREN compte 9 chiffres." }),
      siret: z.string().regex(/^\d{14}$/, { message: "Le SIRET compte 14 chiffres." }),
      ape: z.string().regex(/^\d{2}\.\d{2}[A-Z]$/, { message: "Code APE au format 70.22Z." }),
      registeredAt: IsoDate,
      withDocuments: z.boolean(),
      // ── complete identity (additive) ──
      source: z.enum(["registry", "manual"]).optional(),
      name: z.string().trim().min(1, { message: "Dénomination obligatoire." }).max(200).optional(),
      legalForm: z.string().trim().min(1, { message: "Forme juridique obligatoire." }).max(80).optional(),
      address: z.string().trim().min(5, { message: "Adresse du siège obligatoire." }).max(300).optional(),
      capitalCents: Cents.nonnegative().nullable().optional(),
      apeLabel: z.string().trim().max(300).optional(),
    }),
    response: BoDossier, permission: W,
  }),
  /** BAN address search (api-adresse.data.gouv.fr) for the registered office typed in « Enregistrer l'immatriculation ». */
  addressSearch: adminEndpoint({
    id: "admin.creation.addressSearch", method: "GET", path: "/admin/creation/address-search", query: z.object({ q: z.string().trim().min(3) }),
    response: z.object({ results: z.array(z.object({ label: z.string(), street: z.string(), postcode: z.string(), city: z.string() })) }), permission: R,
  }),
  /** API gap: Kbis / synthèse de dépôt INPI / avis SIRENE deposited by Melrys (« Déposer » / « Remplacer »). */
  uploadOfficialDocument: adminEndpoint({
    id: "admin.creation.uploadOfficialDocument", method: "POST", path: `${B}/official-documents/:kind`, params: D.extend({ kind: z.enum(["kbis", "inpi_receipt", "sirene"]) }),
    body: z.object({ name: z.string().min(1), sizeBytes: z.number().int().nonnegative(), mimeType: z.string() }), response: BoDossier, permission: W,
  }),

  // ── Endings ──
  /** = reject(ctx, id, by, reason). By Melrys: admin only (« Refuser le dossier (Melrys) »); by the INPI: « L'INPI a refusé ». */
  reject: adminEndpoint({
    id: "admin.creation.reject", method: "POST", path: `${B}/reject`, params: D,
    body: z.object({ by: z.enum(["melrys", "inpi"]), reason: z.string().trim().min(20, { message: "20 caractères minimum." }).max(1000) }), response: BoDossier, permission: W,
  }),
  /** API gap: cancellation on behalf of the client (board 96 grid; the refund stays « en attente d'approbation » until Finance issues it). */
  cancel: adminEndpoint({
    id: "admin.creation.cancel", method: "POST", path: `${B}/cancel`, params: D,
    body: z.object({ reason: z.string().trim().min(3, { message: "3 caractères minimum." }).max(500) }), response: BoDossier, permission: W,
  }),
  /** API gap: « Rouvrir le dossier » after a rejection — only the agent in charge; back to the state before the rejection. */
  reopen: adminEndpoint({
    id: "admin.creation.reopen", method: "POST", path: `${B}/reopen`, params: D,
    body: z.object({ reason: z.string().trim().min(20, { message: "20 caractères minimum." }).max(1000) }), response: BoDossier, permission: W,
  }),
  /** API gap: « Transition manuelle d'état » — admin override, traced with its reason. */
  forceTransition: adminEndpoint({
    id: "admin.creation.forceTransition", method: "POST", path: `${B}/transition`, params: D,
    body: z.object({ state: TrackingState, reason: z.string().trim().min(20, { message: "20 caractères minimum." }).max(1000) }), response: BoDossier, permission: ADMIN_ONLY,
  }),

  // ── Compte client (BO-Mix-Client) ──
  clientAccount: adminEndpoint({ id: "admin.creation.clientAccount", method: "GET", path: "/admin/clients/:companyId/account", params: z.object({ companyId: Id }), response: ClientAccount, permission: ["cabinet.clients", "read"] }),
  /** API gap: sign-up link for a second company (one account = one company). */
  sendSecondCompanyLink: adminEndpoint({
    id: "admin.creation.sendSecondCompanyLink", method: "POST", path: "/admin/clients/:companyId/second-company-link", params: z.object({ companyId: Id }),
    body: z.object({ email: Email, service: z.enum(["creation", "accounting", "invoicing"]), message: z.string().max(1000).nullable() }),
    response: Ok, permission: ["cabinet.clients", "write"],
  }),
} satisfies EndpointGroup;
